import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSettings } from '@wu/widget-core';
import type { Interval } from 'date-fns';
import { formatRFC3339, parseISO } from 'date-fns';
import { isCalendarIntegrationEnabled } from '../../common/is-calendar-integration-enabled';
import { APPOINTMENT_BOOKING_API_BASE_URL } from '../../constants/constants';
import { useEventContext } from '../event-provider';
import { useServices } from '../service/services-provider';
import { getSessionId } from '../utils/get-session-id';

export type CalendarReservedTimeslotResponseItem = {
  from: string;
  to: string;
  timeZone: string;
};

export function useCalendarReservedTimeslotsQuery({ start, end }: Interval) {
  const {
    integrationGoogleCalendarAuth,
    integrationGoogleCalendarCalendar,
    widgetToken
  } = useSettings();
  const queryClient = useQueryClient();
  const { subscribe } = useEventContext();
  const { currentService } = useServices();
  const appointmentServiceId = currentService?.appointmentServiceId ?? '';

  useEffect(() => {
    const unsubscribe = subscribe('resetAll', () => {
      queryClient.resetQueries({ queryKey: ['calendar-reserved-timeslots'] });
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const connectionPid = integrationGoogleCalendarAuth?.identity;
  const calendarId = integrationGoogleCalendarCalendar?.id;
  const isGCEnabled = isCalendarIntegrationEnabled({
    integrationGoogleCalendarAuth,
    integrationGoogleCalendarCalendar
  });

  const enabled = [
    isGCEnabled,
    widgetToken,
    calendarId,
    connectionPid,
    appointmentServiceId
  ].every(Boolean);

  return useQuery<Interval[]>({
    queryKey: [
      'calendar-reserved-timeslots',
      widgetToken,
      connectionPid,
      calendarId,
      appointmentServiceId,
      start,
      end
    ],
    queryFn: async () => {
      const calendarIdEncoded = encodeURIComponent(calendarId!);

      const params = new URLSearchParams({
        connection: connectionPid!,
        calendarId: calendarIdEncoded,
        appointmentServiceId,
        from: formatRFC3339(start),
        to: formatRFC3339(end)
      });

      const response = await fetch(
        `${APPOINTMENT_BOOKING_API_BASE_URL}/appointment-booking/timeslots/reserved?${params}`,
        {
          method: 'GET',
          headers: new Headers({
            'x-session-id': getSessionId(),
            'x-widget-token': widgetToken || ''
          })
        }
      );

      const data = (await response.json()) as unknown as {
        payload: CalendarReservedTimeslotResponseItem[];
        code: number;
        message: string;
      };

      const removeTimeZoneRegexp = /([+-]\d{2}:\d{2}|Z)$/;
      const intervals: Interval[] = data.payload.map(({ from, to }) => {
        return {
          start: parseISO(from.replace(removeTimeZoneRegexp, '')),
          end: parseISO(to.replace(removeTimeZoneRegexp, ''))
        };
      });

      return intervals;
    },
    enabled,
    refetchInterval: 1000 * 60 * 10,
    refetchOnWindowFocus: true
  });
}
